window.BundleBuilder = (() => {
  window.__ttBundleBuilderSuppressCartDrawer = false;

  document.addEventListener(
    'variant:add',
    (event) => {
      if (
        window.__ttBundleBuilderSuppressCartDrawer &&
        event.detail
      ) {
        event.detail.blockCartDrawerOpening = true;
      }
    },
    true
  );

  const rows = new Map();
  let latestCart = null;
  const pendingQuantities = new Map();
  const activeRequests = new Map();
  const planRequests = new Set();

  function showError(state, message = '') {
    const error = state.card?.querySelector(
      '[data-purchase-option-error]'
    );

    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
  }

  async function changePurchaseOption(
    state,
    planId
  ) {
    if (
      planRequests.has(state.variantId)
    ) {
      return;
    }

    planRequests.add(state.variantId);
    showError(state);
    renderAll();

    try {
      latestCart =
        await window.TTCart
          .setVariantSellingPlan(
            state.variantId,
            planId
          );
    } catch (error) {
      showError(
        state,
        error.message ||
          'Could not update purchase option. Please retry.'
      );

      try {
        latestCart =
          await window.TTCart.fetchCart();
      } catch (fetchError) {
        console.error(fetchError);
      }
    } finally {
      planRequests.delete(
        state.variantId
      );

      renderAll();
    }
  }

  function clampMin(value, min) {
    return Math.max(min, value);
  }

  function isTrue(value) {
    return (
      value === true ||
      value === 'true' ||
      value === '1'
    );
  }

  function getRowState(row) {
    const card =
      row.closest('.horizontal-product');

    const bundleRoot =
      row.closest(
        '[data-bundle-builder]'
      );

    const input =
      row.querySelector(
        '[data-qty-input]'
      );

    const decreaseButton =
      row.querySelector(
        '[data-qty-decrease]'
      );

    const increaseButton =
      row.querySelector(
        '[data-qty-increase]'
      );

    const quickBuy =
      card?.querySelector(
        '[data-quick-buy-cta]'
      );

    const quickAddToggle =
      card?.querySelector(
        '[data-quick-add-toggle]'
      );

    const purchaseOptions =
      card?.querySelector(
        '[data-purchase-options]'
      );

    const purchaseOptionSelector =
      card?.querySelector(
        '[data-purchase-option-selector]'
      );

    const checkoutButton =
      bundleRoot?.querySelector(
        'button[name="checkout"]'
      );

    const variantId =
      Number(row.dataset.variantId);

    const excludeFromBundle =
      isTrue(
        row.dataset.excludeFromBundle
      );

    const moneyFormat =
      bundleRoot?.dataset.moneyFormat ||
      window.themeVariables?.settings
        ?.moneyFormat ||
      window.Shopify?.money_format ||
      '${{amount}}';

    return {
      row,
      card,
      bundleRoot,
      input,
      decreaseButton,
      increaseButton,
      quickBuy,
      quickAddToggle,
      purchaseOptions,
      purchaseOptionSelector,
      checkoutButton,
      variantId,
      activeSellingPlanId: null,
      excludeFromBundle,
      moneyFormat
    };
  }

  function formatMoney(
    cents,
    moneyFormat
  ) {
    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney ===
        'function'
    ) {
      return window.Shopify.formatMoney(
        cents,
        moneyFormat
      );
    }

    const amount =
      (cents / 100).toFixed(2);

    return moneyFormat.replace(
      /\{\{\s*amount(?:_with_comma_separator|_no_decimals|_no_decimals_with_comma_separator)?\s*\}\}/,
      amount
    );
  }

  function getCartQuantity(
    variantId
  ) {
    if (
      !latestCart ||
      !window.TTCart
    ) {
      return 0;
    }

    return window.TTCart
      .getQuantityForVariant(
        latestCart,
        variantId,
        true
      );
  }

  function getVariantLines(
    cart,
    variantId
  ) {
    return (
      cart?.items || []
    ).filter(
      (item) =>
        Number(
          item.variant_id || item.id
        ) === Number(variantId)
    );
  }

  function getBundleQuantity() {
    return getBundleQuantityFromCart(
      latestCart
    );
  }

  function getBundleQuantityFromCart(
    cart
  ) {
    if (
      !cart ||
      !window.TTCart
    ) {
      return 0;
    }

    let total = 0;
    const counted = new Set();

    rows.forEach((state) => {
      if (
        state.excludeFromBundle ||
        counted.has(state.variantId)
      ) {
        return;
      }

      counted.add(state.variantId);

      total +=
        window.TTCart
          .getQuantityForVariant(
            cart,
            state.variantId,
            true
          );
    });

    return total;
  }

  function getBundleVariantIds() {
    const ids = [];

    rows.forEach((state) => {
      if (
        state.excludeFromBundle
      ) {
        return;
      }

      ids.push(state.variantId);
    });

    return ids;
  }

  function updateBundleTotalsForRoot(
    bundleRoot,
    cart
  ) {
    if (
      !bundleRoot ||
      !cart
    ) {
      return;
    }

    const totalElement =
      bundleRoot.querySelector(
        '[id^="bundle-total-"]'
      );

    const originalElement =
      bundleRoot.querySelector(
        '[id^="bundle-original-"]'
      );

    if (!totalElement) return;

    const moneyFormat =
      bundleRoot.dataset.moneyFormat ||
      window.themeVariables?.settings
        ?.moneyFormat ||
      window.Shopify?.money_format ||
      '${{amount}}';

    const cartTotal =
      cart.total_price || 0;

    const originalSubtotal =
      (cart.items || []).reduce(
        (sum, item) =>
          sum +
          (
            item.original_line_price ||
            item.price * item.quantity
          ),
        0
      );

    totalElement.innerHTML =
      formatMoney(
        cartTotal,
        moneyFormat
      );

    if (!originalElement) return;

    if (
      originalSubtotal >
      cartTotal
    ) {
      originalElement.innerHTML =
        formatMoney(
          originalSubtotal,
          moneyFormat
        );

      originalElement.hidden = false;
    } else {
      originalElement.innerHTML = '';
      originalElement.hidden = true;
    }
  }

  function dispatchBundleQuantityChange() {
    document.dispatchEvent(
      new CustomEvent(
        'bundle:quantity-change',
        {
          bubbles: true,
          detail: {
            quantity:
              getBundleQuantity(),
            cart: latestCart
          }
        }
      )
    );
  }

  function renderAll(cart = null) {
    latestCart =
      cart || latestCart;

    if (!latestCart) return;

    rows.forEach((state) => {
      const cartQuantity =
        getCartQuantity(
          state.variantId
        );

      const variantLines =
        getVariantLines(
          latestCart,
          state.variantId
        );

      const hasPending =
        pendingQuantities.has(
          state.variantId
        );

      const isBusy =
        hasPending ||
        planRequests.has(
          state.variantId
        );

      if (
        variantLines.length > 0 &&
        !isBusy
      ) {
        state.activeSellingPlanId =
          Number(
            variantLines[0]
              .selling_plan_allocation
              ?.selling_plan?.id
          ) || null;

        if (
          state.purchaseOptionSelector
        ) {
          state.purchaseOptionSelector
            .value =
              state.activeSellingPlanId
                ? String(
                    state
                      .activeSellingPlanId
                  )
                : '';
        }
      }

      const displayedQuantity =
        hasPending
          ? pendingQuantities.get(
              state.variantId
            )
          : cartQuantity;

      state.input.value =
        displayedQuantity;

      state.input.disabled =
        planRequests.has(
          state.variantId
        );

      if (state.decreaseButton) {
        state.decreaseButton.disabled =
          planRequests.has(
            state.variantId
          );
      }

      if (state.increaseButton) {
        state.increaseButton.disabled =
          planRequests.has(
            state.variantId
          );
      }

      if (state.quickBuy) {
        /*
         * Before adding:
         * - show Add to Bundle
         * - hide quantity
         * - hide selling-plan select
         *
         * After adding:
         * - hide Add to Bundle
         * - show quantity
         * - show selling-plan select
         */
        state.row.hidden =
          displayedQuantity < 1;

        state.quickBuy.hidden =
          displayedQuantity > 0;

        if (state.purchaseOptions) {
          state.purchaseOptions.hidden =
            displayedQuantity < 1;

          state.purchaseOptions.style.display =
            displayedQuantity < 1
              ? 'none'
              : '';
        }

        if (
          state.quickAddToggle
        ) {
          state.quickAddToggle.hidden =
            displayedQuantity > 0;

          state.quickAddToggle.disabled =
            isBusy;
        }

        if (
          state.purchaseOptionSelector
        ) {
          state.purchaseOptionSelector
            .disabled = isBusy;
        }
      } else {
        state.row.hidden = false;
      }

      if (state.card) {
        state.card.classList.toggle(
          'is-selected',
          displayedQuantity > 0
        );

        state.card.classList.toggle(
          'is-updating',
          hasPending
        );

        state.card.classList.toggle(
          'is-excluded-from-bundle',
          state.excludeFromBundle
        );
      }

      if (state.checkoutButton) {
        state.checkoutButton.disabled =
          pendingQuantities.size > 0 ||
          planRequests.size > 0;
      }
    });

    const updatedRoots =
      new Set();

    rows.forEach((state) => {
      if (
        state.bundleRoot &&
        !updatedRoots.has(
          state.bundleRoot
        )
      ) {
        updatedRoots.add(
          state.bundleRoot
        );

        updateBundleTotalsForRoot(
          state.bundleRoot,
          latestCart
        );
      }
    });

    dispatchBundleQuantityChange();

    if (window.TTCartProgress) {
      window.TTCartProgress.update(
        latestCart
      );
    }
  }

  async function changeVariantQuantity(
    variantId,
    nextQuantity,
    sellingPlanId = null
  ) {
    const safeQuantity =
      clampMin(
        nextQuantity,
        0
      );

    pendingQuantities.set(
      variantId,
      safeQuantity
    );

    renderAll();

    if (
      activeRequests.has(variantId)
    ) {
      return;
    }

    activeRequests.set(
      variantId,
      true
    );

    window
      .__ttBundleBuilderSuppressCartDrawer =
        true;

    try {
      while (
        pendingQuantities.has(
          variantId
        )
      ) {
        const quantityToApply =
          pendingQuantities.get(
            variantId
          );

        const updatedCart =
          await window.TTCart
            .setBundleVariantQuantity(
              variantId,
              quantityToApply,
              sellingPlanId
            );

        latestCart = updatedCart;

        const cartQuantity =
          getCartQuantity(variantId);

        if (
          pendingQuantities.get(
            variantId
          ) === quantityToApply
        ) {
          pendingQuantities.delete(
            variantId
          );

          if (
            cartQuantity !==
            quantityToApply
          ) {
            rows.forEach((state) => {
              if (
                state.variantId ===
                variantId
              ) {
                showError(
                  state,
                  'The cart adjusted the requested quantity.'
                );
              }
            });
          }
        }

        renderAll(latestCart);
      }
    } catch (error) {
      console.error(error);

      pendingQuantities.delete(
        variantId
      );

      rows.forEach((state) => {
        if (
          state.variantId ===
          variantId
        ) {
          showError(
            state,
            error.message ||
              'Could not update cart.'
          );
        }
      });

      try {
        const cart =
          await window.TTCart
            .fetchCart();

        renderAll(cart);
      } catch (fetchError) {
        console.error(fetchError);
        renderAll();
      }
    } finally {
      activeRequests.delete(
        variantId
      );

      setTimeout(() => {
        window
          .__ttBundleBuilderSuppressCartDrawer =
            false;
      }, 250);
    }
  }

  function initQuantityRow(row) {
    if (
      !row ||
      row.__ttQuantityRowInitialized
    ) {
      return;
    }

    const state =
      getRowState(row);

    if (
      !state.input ||
      !state.variantId ||
      !window.TTCart
    ) {
      return;
    }

    row.__ttQuantityRowInitialized =
      true;

    rows.set(row, state);

    if (state.quickAddToggle) {
      state.quickAddToggle
        .addEventListener(
          'click',
          async (event) => {
            event.preventDefault();
            event.stopPropagation();
            event
              .stopImmediatePropagation();

            state.quickAddToggle
              .setAttribute(
                'aria-busy',
                'true'
              );

            const currentQuantity =
              getCartQuantity(
                state.variantId
              );

            /*
             * The select is hidden before
             * the product is added, so the
             * first addition is one-time.
             */
            const selectedPlanId =
              null;

            state.activeSellingPlanId =
              selectedPlanId;

            try {
              await changeVariantQuantity(
                state.variantId,
                currentQuantity + 1,
                selectedPlanId
              );
            } finally {
              state.quickAddToggle
                .removeAttribute(
                  'aria-busy'
                );
            }
          }
        );
    }

    if (
      state.purchaseOptionSelector
    ) {
      state.purchaseOptionSelector
        .addEventListener(
          'change',
          async () => {
            const selectedPlanId =
              Number(
                state
                  .purchaseOptionSelector
                  .value
              ) || null;

            const currentQuantity =
              getCartQuantity(
                state.variantId
              );

            state.activeSellingPlanId =
              selectedPlanId;

            if (
              currentQuantity < 1
            ) {
              return;
            }

            await changePurchaseOption(
              state,
              selectedPlanId
            );
          }
        );
    }

    state.input.addEventListener(
      'input',
      () => {
        state.input.value =
          state.input.value.replace(
            /[^\d]/g,
            ''
          );
      }
    );

    state.input.addEventListener(
      'change',
      () => {
        if (
          planRequests.has(
            state.variantId
          )
        ) {
          renderAll();
          return;
        }

        const targetQuantity =
          clampMin(
            parseInt(
              state.input.value ||
                '0',
              10
            ) || 0,
            0
          );

        changeVariantQuantity(
          state.variantId,
          targetQuantity,
          state.activeSellingPlanId
        );
      }
    );

    if (state.decreaseButton) {
      state.decreaseButton
        .addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            event
              .stopImmediatePropagation();

            requestAnimationFrame(
              () => {
                state.input
                  .dispatchEvent(
                    new Event(
                      'change',
                      {
                        bubbles: true
                      }
                    )
                  );
              }
            );
          }
        );
    }

    if (state.increaseButton) {
      state.increaseButton
        .addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            event
              .stopImmediatePropagation();

            requestAnimationFrame(
              () => {
                state.input
                  .dispatchEvent(
                    new Event(
                      'change',
                      {
                        bubbles: true
                      }
                    )
                  );
              }
            );
          }
        );
    }
  }

  function reconcilePending(cart) {
    rows.forEach((state) => {
      const cartQuantity =
        window.TTCart
          .getQuantityForVariant(
            cart,
            state.variantId,
            true
          );

      if (
        pendingQuantities.has(
          state.variantId
        ) &&
        !activeRequests.has(
          state.variantId
        )
      ) {
        const pendingQuantity =
          pendingQuantities.get(
            state.variantId
          );

        if (
          pendingQuantity ===
          cartQuantity
        ) {
          pendingQuantities.delete(
            state.variantId
          );
        }
      }
    });
  }

  async function sync() {
    if (!window.TTCart) return;

    try {
      const cart =
        await window.TTCart
          .fetchCart();

      latestCart = cart;

      reconcilePending(latestCart);
      renderAll(latestCart);
    } catch (error) {
      console.error(error);
    }
  }

  function autoInit() {
    rows.forEach(
      (state, row) => {
        if (!row.isConnected) {
          rows.delete(row);
        }
      }
    );

    document
      .querySelectorAll(
        '[data-cart-quantity-row]'
      )
      .forEach(initQuantityRow);

    sync();
  }

  document.addEventListener(
    'DOMContentLoaded',
    autoInit
  );

  if (
    document.readyState !==
    'loading'
  ) {
    autoInit();
  }

  document.addEventListener(
    'shopify:section:load',
    autoInit
  );

  document.addEventListener(
    'ttcart:ready',
    autoInit
  );

  document.addEventListener(
    'cart:change',
    (event) => {
      if (
        event?.detail?.cart
      ) {
        latestCart =
          event.detail.cart;

        reconcilePending(
          latestCart
        );

        renderAll(latestCart);
      } else {
        sync();
      }
    }
  );

  return {
    init: autoInit,
    sync,
    getBundleQuantity,
    getBundleQuantityFromCart,
    getBundleVariantIds
  };
})();